import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { auditListQueryOptions } from '@/lib/audit';

const PREVIEW_LIMIT = 5;

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
}

/**
 * Security-visibility widget for users with `audit.read`. Reads the
 * same `auth.login_failed` rows the audit log shows but renders only
 * when there's something to see — silence is the right signal on a
 * normal day.
 *
 * Pairs with the audit page's quick-action chip for the same code:
 * "ดูทั้งหมด" link jumps there pre-filtered for deeper investigation
 * (IP correlation, time clustering, etc.).
 *
 * Metadata shape from /login: { email } when known, otherwise empty.
 * Falls back to "anonymous" when neither metadata.email nor a
 * userName from the join is present (typical for failed-login on an
 * unknown address — the dummy-verify timing equalizer ensures the
 * row still gets recorded).
 */
export function FailedLoginsCard() {
  const query = useQuery(
    auditListQueryOptions({
      action: 'auth.login_failed',
      perPage: PREVIEW_LIMIT,
    }),
  );
  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;

  if (!query.isLoading && items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-red-500" />
          login ที่ล้มเหลว
          {total > 0 && (
            <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
              {total}
            </span>
          )}
        </CardTitle>
        <Link
          to="/audit"
          search={{ action: 'auth.login_failed' }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {items.map((a) => {
              const metaEmail =
                typeof a.metadata === 'object' &&
                a.metadata !== null &&
                'email' in a.metadata &&
                typeof a.metadata.email === 'string'
                  ? a.metadata.email
                  : null;
              const who = metaEmail ?? a.userName ?? 'anonymous';
              return (
                <li
                  key={a.id}
                  className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-1.5 last:border-0 dark:border-slate-800"
                >
                  <span className="truncate font-mono text-slate-700 dark:text-slate-200">
                    {who}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {a.ipAddress ?? '—'} · {formatRelative(a.createdAt)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
